import { format, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { create, group, only, skipWhen } from "vest";

import {
  EVENT_TYPE_APPOINTMENT,
  EVENT_TYPE_APPOINTMENT_BLOCK,
  EVENT_TYPE_CLINIC_EVENT,
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_PERSONAL_WORKSHIFT,
  REOCCURRENCE_PERIOD_TYPE_DAILY,
  REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH
} from "constants/appointments";
import { AppointmentType } from "services/types/appointment-types.type";
import { Appointment } from "services/types/appointments.type";
import { type AssignedTag } from "services/types/assigned-tag.type";
import { Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import { Room } from "services/types/rooms.type";
import { Schedule } from "services/types/schedules.type";
import { MinimalStaffMember } from "services/types/staff.type";
import {
  dateIsValid,
  required,
  roomIsAvailable
} from "utils/validations.utils";

import { AppointmentFormValues } from "./appointment-form.component";

export type AppointmentFormFieldName =
  | "title"
  | "clinic"
  | "is_recurring"
  | "event_type"
  | "recurrence_period"
  | "recurrence_day"
  | "recurrence_week"
  | "appointment_type"
  | "companion_type"
  | "companion_name"
  | "event_end_date"
  | "appointment_date"
  | "start_time"
  | "end_time"
  | "staff_member"
  | "room"
  | "patient"
  | "referral"
  | "should_notify"
  | "notes"
  | "schedule.rrule"
  | "allow_room_conflict"
  | "show_room_warning"
  | "sub_referral_source";

export const appointmentFormValidator = ({
  isReferralSourceRequired,
  isSubReferralSourceRequired
}: {
  isReferralSourceRequired: boolean;
  isSubReferralSourceRequired: boolean;
}) =>
  create(
    ({
      title,
      clinic,
      event_type,
      appointment_type,
      staff_member,
      referral,
      appointment_date,
      recurrence_period,
      recurrence_day,
      recurrence_week,
      patient,
      is_recurring,
      should_create_intake_form_request,
      intake_form_request,
      id,
      rrule,
      event_end_date,
      referral_source,
      sub_referral_source,
      sub_referral_source_object,
      sub_referral_source_required,
      room,
      allow_room_conflict,
      show_room_warning,
      schedule
    }: AppointmentFormValues) => {
      only.group(event_type);

      const existingAppointment = Boolean(id);

      group(EVENT_TYPE_APPOINTMENT, () => {
        required<AppointmentFormFieldName>(
          "patient" as AppointmentFormFieldName,
          "Patient",
          patient
        );
        required<AppointmentFormFieldName>(
          "appointment_type" as AppointmentFormFieldName,
          "Appointment Type",
          appointment_type
        );
        required<AppointmentFormFieldName>(
          "staff_member" as AppointmentFormFieldName,
          "Staff",
          staff_member
        );
        dateIsValid<AppointmentFormFieldName>(
          "appointment_date" as AppointmentFormFieldName,
          "Date",
          appointment_date
        );
        required<AppointmentFormFieldName>(
          "clinic" as AppointmentFormFieldName,
          "Clinic",
          clinic
        );
        required<AppointmentFormFieldName>(
          "referral" as AppointmentFormFieldName,
          "Physician referral",
          referral
        );

        skipWhen(!isReferralSourceRequired, () => {
          required<AppointmentFormFieldName>(
            "referral_source" as AppointmentFormFieldName,
            "Referral Source",
            referral_source
          );
        });

        if (isSubReferralSourceRequired && sub_referral_source_required) {
          required<AppointmentFormFieldName>(
            "sub_referral_source" as AppointmentFormFieldName,
            "Sub-Referral Source",
            sub_referral_source || sub_referral_source_object
          );
        }

        skipWhen(!is_recurring, () => {
          required<AppointmentFormFieldName>(
            "event_end_date" as AppointmentFormFieldName,
            "Date End",
            event_end_date ? event_end_date.toString() : event_end_date
          );

          dateIsValid<AppointmentFormFieldName>(
            "event_end_date" as AppointmentFormFieldName,
            "Date End",
            event_end_date
          );
        });

        skipWhen(!room || !show_room_warning, () => {
          roomIsAvailable<AppointmentFormFieldName>(
            "allow_room_conflict" as AppointmentFormFieldName,
            "Room Availability",
            allow_room_conflict
          );
        });

        skipWhen(
          existingAppointment || !should_create_intake_form_request,
          () => {
            required<AppointmentFormFieldName>(
              "intake_form_request.intake_form_request_submissions.create" as AppointmentFormFieldName,
              "Onboarding Forms",
              intake_form_request.intake_form_request_submissions.create
            );
          }
        );
      });

      group(EVENT_TYPE_PERSONAL_EVENT, () => {
        dateIsValid<AppointmentFormFieldName>(
          "appointment_date" as AppointmentFormFieldName,
          "Date",
          appointment_date
        );
        required<AppointmentFormFieldName>(
          "staff_member" as AppointmentFormFieldName,
          "Staff",
          staff_member
        );
        required<AppointmentFormFieldName>(
          "title" as AppointmentFormFieldName,
          "Title",
          title
        );
        required<AppointmentFormFieldName>(
          "clinic" as AppointmentFormFieldName,
          "Clinic",
          clinic
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !== REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            dateIsValid<AppointmentFormFieldName>(
              "event_end_date" as AppointmentFormFieldName,
              "Date End",
              event_end_date
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period === REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_day" as AppointmentFormFieldName,
              "Recurrence Day",
              recurrence_day
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !==
              REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_week" as AppointmentFormFieldName,
              "Recurrence Week",
              recurrence_week
            );
          }
        );
      });

      group(EVENT_TYPE_CLINIC_EVENT, () => {
        dateIsValid<AppointmentFormFieldName>(
          "appointment_date" as AppointmentFormFieldName,
          "Date",
          appointment_date
        );
        required<AppointmentFormFieldName>(
          "title" as AppointmentFormFieldName,
          "Title",
          title
        );
        required<AppointmentFormFieldName>(
          "clinic" as AppointmentFormFieldName,
          "Clinic",
          clinic
        );
        skipWhen(
          !is_recurring ||
            recurrence_period === REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_day" as AppointmentFormFieldName,
              "Recurrence Day",
              recurrence_day
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !== REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            dateIsValid<AppointmentFormFieldName>(
              "event_end_date" as AppointmentFormFieldName,
              "Date End",
              event_end_date
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !==
              REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_week" as AppointmentFormFieldName,
              "Recurrence Week",
              recurrence_week
            );
          }
        );
      });

      group(EVENT_TYPE_PERSONAL_WORKSHIFT, () => {
        dateIsValid<AppointmentFormFieldName>(
          "appointment_date" as AppointmentFormFieldName,
          "Date",
          appointment_date
        );
        required<AppointmentFormFieldName>(
          "staff_member" as AppointmentFormFieldName,
          "Staff",
          staff_member
        );
        required<AppointmentFormFieldName>(
          "title" as AppointmentFormFieldName,
          "Title",
          title
        );
        required<AppointmentFormFieldName>(
          "clinic" as AppointmentFormFieldName,
          "Clinic",
          clinic
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !== REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            dateIsValid<AppointmentFormFieldName>(
              "event_end_date" as AppointmentFormFieldName,
              "Date End",
              event_end_date
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period === REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_day" as AppointmentFormFieldName,
              "Recurrence Day",
              recurrence_day
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !==
              REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_week" as AppointmentFormFieldName,
              "Recurrence Week",
              recurrence_week
            );
          }
        );
      });

      group(EVENT_TYPE_APPOINTMENT_BLOCK, () => {
        required<AppointmentFormFieldName>(
          "title" as AppointmentFormFieldName,
          "Title",
          title
        );
        required<AppointmentFormFieldName>(
          "appointment_type" as AppointmentFormFieldName,
          "Appointment Type",
          appointment_type
        );
        required<AppointmentFormFieldName>(
          "staff_member" as AppointmentFormFieldName,
          "Staff",
          staff_member
        );
        dateIsValid<AppointmentFormFieldName>(
          "appointment_date" as AppointmentFormFieldName,
          "Date",
          appointment_date
        );
        required<AppointmentFormFieldName>(
          "clinic" as AppointmentFormFieldName,
          "Clinic",
          clinic
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !== REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            dateIsValid<AppointmentFormFieldName>(
              "event_end_date" as AppointmentFormFieldName,
              "Date End",
              event_end_date
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period === REOCCURRENCE_PERIOD_TYPE_DAILY ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_day" as AppointmentFormFieldName,
              "Recurrence Day",
              recurrence_day
            );
          }
        );
        skipWhen(
          !is_recurring ||
            recurrence_period !==
              REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH ||
            Boolean(rrule) ||
            Boolean(schedule?.rrule),
          () => {
            required<AppointmentFormFieldName>(
              "recurrence_week" as AppointmentFormFieldName,
              "Recurrence Week",
              recurrence_week
            );
          }
        );
      });
    }
  );

export const formatAppointment = (data: AppointmentFormValues) => {
  const timezone =
    data.timezone ||
    data.clinic?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const rrule = data.schedule?.rrule || null;

  const result: Partial<Appointment> = {
    id: data.id ?? undefined,
    title: data.title,
    type: "Appointment",
    appointmentType: data.appointment_type as AppointmentType,
    notes: data.notes,
    room: data.room as Room,
    patient: data.patient as Patient,
    companionName: data.companion_name || "",
    clinic: data.clinic || undefined,
    companionType: data.companion_type || null,
    companion_present: Boolean(data.companion_type),
    endDate: format(data.end_time, "yyyy-MM-dd HH:mm:ssXXX"),
    startDate: format(data.start_time, "yyyy-MM-dd HH:mm:ssXXX"),
    referralSource: data.referral_source === "" ? null : data.referral_source,
    subReferralSource:
      !data.referral_source || !data.sub_referral_source
        ? null
        : data.sub_referral_source,
    referral: data.referral,
    subReferralSourceObject: data.sub_referral_source_object,
    staffMember: data.staff_member as MinimalStaffMember,
    staffMemberId: (data.staff_member as MinimalStaffMember).id,
    supervising_provider: data.supervising_provider
      ? (data.supervising_provider as MinimalStaffMember)
      : null,
    is_direct_supervisor: data.is_direct_supervisor,
    shouldNotify: data.should_notify,
    shouldCreateScheduleForStaff: data.should_create_schedule_for_staff,
    overwriteBlock: data.overwrite_block,
    allowOverlap: data.allow_overlap,
    referringPhysician: data.referring_physician,
    notificationMethod: data.notification_method ?? undefined,
    should_create_intake_form_request: data.should_create_intake_form_request,
    intake_form_request: data.intake_form_request,
    rescheduled_appointment_id: data.rescheduled_appointment_id,

    schedule: data.is_recurring
      ? {
          rrule,
          clinic: data.clinic?.id,
          recurrence_period: rrule ? null : data.recurrence_period,
          recurrence_day: rrule
            ? null
            : data.recurrence_day
              ? Number(data.recurrence_day)
              : null,
          recurrence_week: rrule
            ? null
            : data.recurrence_week
              ? Number(data.recurrence_week)
              : null,
          start_time: formatInTimeZone(data.start_time, timezone, "HH:mm"),
          end_time: formatInTimeZone(data.end_time, timezone, "HH:mm"),
          start_date: format(data.start_time, "yyyy-MM-dd"),
          end_date:
            data.event_end_date && isValid(data.event_end_date)
              ? format(data.event_end_date, "yyyy-MM-dd")
              : null
        }
      : null
  };

  return result;
};

export const formatPersonalEvent = ({
  data,
  clinic,
  user,
  eventType
}: {
  data: AppointmentFormValues;
  clinic: Clinic;
  user: MinimalStaffMember;
  eventType: "Personal" | "Clinic" | "WorkShift";
}) => {
  const isPersonalEvent = eventType === "Personal" || eventType === "WorkShift";
  const isPersonalWorkShift = eventType === "WorkShift";
  const isClinicEvent = eventType === "Clinic";

  const rrule = data.schedule?.rrule || null;

  const timezone =
    data.timezone ||
    data.clinic?.timezone ||
    clinic.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const result: Schedule = {
    id: data.modelId ?? undefined,
    title: data.title,
    type: eventType,
    is_available: isPersonalWorkShift,
    start_time: formatInTimeZone(data.start_time, timezone, "HH:mm"),
    end_time: formatInTimeZone(data.end_time, timezone, "HH:mm"),
    start_date: formatInTimeZone(data.start_time, timezone, "yyyy-MM-dd"),
    end_date:
      data.event_end_date && isValid(data.event_end_date)
        ? formatInTimeZone(data.event_end_date, timezone, "yyyy-MM-dd")
        : null,
    clinic: (data.clinic || clinic).id,
    notes: data.notes,
    staff_member: isPersonalEvent ? data.staff_member?.id || user.id : null,
    staff_members: isClinicEvent
      ? {
          add: data.staff_members?.map((staff_member) => staff_member.id) ?? [],
          remove: data.modelId
            ? (data.removed_staff_members?.map(
                (staff_member) => staff_member.id
              ) ?? [])
            : undefined
        }
      : // staff_members is non-empty only when converting Clinic → Personal: the
        // type-flip useEffect intentionally leaves M2M items in form state so this
        // remove list can be built at save time. For all other non-Clinic saves
        // (vanilla Personal Event, Work Shift) staff_members is always [].
        data.modelId && data.staff_members.length > 0
        ? { remove: data.staff_members.map((staff_member) => staff_member.id) }
        : undefined,
    is_global_practice_event: data.is_global_practice_event,
    appointment_types: data.appointment_type ? [data.appointment_type.id] : []
  };

  if (data.is_recurring) {
    result.rrule = rrule;
    result.recurrence_period = rrule ? null : data.recurrence_period;
    result.recurrence_day = rrule
      ? null
      : data.recurrence_day
        ? Number(data.recurrence_day)
        : null;
    result.recurrence_week = rrule
      ? null
      : data.recurrence_week
        ? Number(data.recurrence_week)
        : null;
  } else {
    result.recurrence_period = null;
    result.recurrence_day = null;
    result.recurrence_week = null;
    result.end_date = format(data.end_time, "yyyy-MM-dd");
  }

  return result;
};

export const formatAppointmentBlock = (
  data: AppointmentFormValues,
  clinic: Clinic
) => {
  const timezone =
    data.timezone ||
    data.clinic?.timezone ||
    clinic.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rrule = data.schedule?.rrule || null;
  const result: Schedule = {
    id: data.modelId ?? undefined,
    title: data.title,
    type: "Block",
    is_available: true,
    start_time: formatInTimeZone(data.start_time, timezone, "HH:mm"),
    end_time: formatInTimeZone(data.end_time, timezone, "HH:mm"),
    start_date: formatInTimeZone(data.start_time, timezone, "yyyy-MM-dd"),
    end_date:
      data.event_end_date && isValid(data.event_end_date)
        ? formatInTimeZone(data.event_end_date, timezone, "yyyy-MM-dd")
        : null,
    clinic: (data.clinic || clinic).id,
    notes: data.notes,
    staff_member: data.staff_member!.id,
    appointment_types: [data.appointment_type!.id]
  };
  if (data.is_recurring) {
    result.rrule = rrule;
    result.recurrence_period = rrule ? null : data.recurrence_period;
    result.recurrence_day = rrule
      ? null
      : data.recurrence_day
        ? Number(data.recurrence_day)
        : null;
    result.recurrence_week = rrule
      ? null
      : data.recurrence_week
        ? Number(data.recurrence_week)
        : null;
  } else {
    result.recurrence_period = null;
    result.recurrence_day = null;
    result.recurrence_week = null;
    result.end_date = format(data.end_time, "yyyy-MM-dd");
  }

  return result;
};

export const getAssignedTagChanges = (
  initial: Array<AssignedTag & { isDirty?: boolean }>,
  formTags: Array<AssignedTag & { isDirty?: boolean }>,
  appointmentId: number
) => {
  const createMapById = (tags: Array<AssignedTag & { isDirty?: boolean }>) =>
    new Map(tags.map((tag) => [tag.id, tag]));

  const initialById = createMapById(initial);
  const formTagsById = createMapById(formTags);
  const recreateById = createMapById([]);

  const result = {
    tagsToAdd: [] as Array<AssignedTag & { isDirty?: boolean }>,
    tagsToUpdate: [] as Array<AssignedTag & { isDirty?: boolean }>,
    tagsToDelete: [] as Array<AssignedTag & { isDirty?: boolean }>
  };

  formTags.forEach((formTag) => {
    const initialTag = initialById.get(formTag.id);

    if ((!initialTag || !initialTag.id) && formTag.isDirty) {
      const existing = initial.find((tag) => tag.tag.id === formTag.tag.id);
      if (existing && String(formTag.id).length === 13) {
        const addTag = {
          ...existing,
          description: formTag.description,
          expires_at: formTag.expires_at,
          object_id: appointmentId
        };
        result.tagsToUpdate.push(addTag);
        recreateById.set(addTag.id, addTag);
      } else {
        result.tagsToAdd.push({ ...formTag, object_id: appointmentId });
      }
    } else if (initialTag && formTag.isDirty) {
      result.tagsToUpdate.push({ ...formTag, object_id: appointmentId });
    }
  });

  initial.forEach((initialTag) => {
    if (!formTagsById.has(initialTag.id) && !recreateById.has(initialTag.id)) {
      result.tagsToDelete.push({ ...initialTag, object_id: appointmentId });
    }
  });

  return result;
};
